/**
 * PropertyMap Component
 * Renders a real Google Maps with pin at seller's property location
 * 
 * Props:
 *   latitude: number - Property latitude
 *   longitude: number - Property longitude
 *   address: string - Property address for display
 *   postcode: string - Property postcode for display
 *   zoom: number - Map zoom level (default: 16)
 */

import React, { useEffect, useRef, useState } from 'react';

/**
 * Load Google Maps API script once globally
 */
let googleMapsScript = null;
let googleMapsReady = false;
let googleMapsCallbacks = [];

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (googleMapsReady) {
      resolve();
      return;
    }

    // Already loading - queue callback
    if (googleMapsScript) {
      googleMapsCallbacks.push({ resolve, reject });
      return;
    }

    // Create script element
    googleMapsScript = document.createElement('script');
    googleMapsScript.async = true;
    googleMapsScript.defer = true;
    googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;

    googleMapsScript.onload = () => {
      googleMapsReady = true;
      console.debug('[PropertyMap] Google Maps API loaded');
      
      // Resolve all pending callbacks
      googleMapsCallbacks.forEach(cb => cb.resolve());
      googleMapsCallbacks = [];
      
      resolve();
    };

    googleMapsScript.onerror = () => {
      console.error('[PropertyMap] Failed to load Google Maps API');
      
      // Reject all pending callbacks
      googleMapsCallbacks.forEach(cb => cb.reject(new Error('Failed to load Google Maps')));
      googleMapsCallbacks = [];
      
      reject(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(googleMapsScript);
    googleMapsCallbacks.push({ resolve, reject });
  });
}

/**
 * PropertyMap Component
 */
export default function PropertyMap({
  latitude,
  longitude,
  address,
  postcode,
  zoom = 16
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Bail if no coordinates
    if (!latitude || !longitude) {
      setError('MISSING_COORDINATES');
      setLoading(false);
      return;
    }

    let mounted = true;

    const initializeMap = async () => {
      try {
        // Get API key from environment
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        
        if (!apiKey) {
          console.error('[PropertyMap] VITE_GOOGLE_MAPS_API_KEY not configured');
          if (mounted) {
            setError('CONFIG_ERROR');
            setLoading(false);
          }
          return;
        }

        // Load Google Maps API
        await loadGoogleMapsScript(apiKey);

        if (!mounted) return;

        // Create map
        const map = new google.maps.Map(containerRef.current, {
          center: { lat: latitude, lng: longitude },
          zoom,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          styles: [
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        mapRef.current = map;

        // Create marker
        const marker = new google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map,
          title: `${address}, ${postcode}`,
          icon: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
        });

        markerRef.current = marker;

        // Info window
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="font-family: sans-serif; font-size: 13px;">
              <strong>${address}</strong><br/>
              ${postcode}
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        // Auto-open info window on load
        infoWindow.open(map, marker);

        if (mounted) {
          setLoading(false);
          setError(null);
          console.debug('[PropertyMap] Property map initialized', {
            lat: latitude,
            lng: longitude
          });
        }
      } catch (err) {
        console.error('[PropertyMap] Failed to initialize map', err);

        if (mounted) {
          setError('INIT_ERROR');
          setLoading(false);
        }
      }
    };

    initializeMap();

    return () => {
      mounted = false;
    };
  }, [latitude, longitude, address, postcode, zoom]);

  // Loading state
  if (loading) {
    return (
      <div
        ref={containerRef}
        className="w-full h-64 bg-slate-100 rounded-lg flex items-center justify-center animate-pulse"
      >
        <p className="text-slate-500 text-sm">Loading map...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full h-64 bg-red-50 rounded-lg border border-red-200 flex flex-col items-center justify-center p-4">
        <p className="text-red-600 text-sm font-medium">Map location unavailable</p>
        <p className="text-red-500 text-xs mt-1">Address will be confirmed by agent</p>
        <p className="text-xs text-red-400 mt-2">
          {address && postcode ? `${address}, ${postcode}` : 'Address pending'}
        </p>
      </div>
    );
  }

  // Success - render map
  return (
    <div
      ref={containerRef}
      className="w-full h-64 rounded-lg border border-white/10 overflow-hidden"
      style={{ backgroundImage: 'linear-gradient(to bottom, #f3f4f6, #e5e7eb)' }}
    />
  );
}

/**
 * Hook to fetch seller onboarding with coordinates
 */
export function useSellerOnboarding(onboardingId) {
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!onboardingId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchOnboarding = async () => {
      try {
        const response = await fetch(`/api/seller/onboarding/${onboardingId}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const json = await response.json();

        if (mounted) {
          setOnboarding(json.data);
          setError(null);
        }
      } catch (err) {
        console.error('[PropertyMap] Failed to fetch seller onboarding', err);

        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load');
          setOnboarding(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchOnboarding();

    return () => {
      mounted = false;
    };
  }, [onboardingId]);

  return { onboarding, loading, error };
}
